import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import {
  Box,
  type BoxProps,
  Button,
  Card,
  Flex,
  Text,
  Title,
  Switch,
} from '@mantine/core'
import React, { type PropsWithChildren } from 'react'

import OpenForBusinessEvent from '@flecto-io/analytics/actions/saas/business-hours/open-for-business'
import OperatingHoursTypeEvent from '@flecto-io/analytics/actions/saas/business-hours/operating-hours-type'
import { useTranslation } from '@flecto/translations'
import { type CoreUserAddressOperatingHours } from '@flecto/types'
import { Icon } from '@flecto/ui'

import { useFormContext } from '../AddressForm'
import { BusinessSchedule } from './BusinessSchedule'

export function AddressBusinessHours(props: PropsWithChildren<BoxProps>) {
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'general' })

  const { value, onChange } = form.getInputProps('open_to_public')

  const handleChange = React.useCallback(() => {
    onChange(!value)
    new OpenForBusinessEvent(
      !value,
      String(form.values.address_label)
    ).sendEvent()
  }, [value, onChange])

  return (
    <Box {...props}>
      <Title order={4} mb='sm'>
        {t('shared.modules.address.about_this_location')}
      </Title>
      <Card>
        <Flex align='center' justify='space-between'>
          <Box>
            <Text fw={500} mb='xs'>
              {t('shared.modules.address.open_to_public')}
            </Text>
            <Text size='sm' color='dimmed'>
              {t('shared.modules.address.open_to_public_desc')}
            </Text>
          </Box>
          <Switch checked={value} onClick={handleChange} />
        </Flex>
        {value && <BusinessHoursOptionsSelector mt='md' />}
      </Card>
    </Box>
  )
}

function BusinessHoursOptionsSelector(props: PropsWithChildren<BoxProps>) {
  const form = useFormContext()

  const { t } = useTranslation('saas', { keyPrefix: 'general' })
  const { value: operating_hours_type } = form.getInputProps(
    'operating_hours_type'
  )

  React.useEffect(() => {
    new OperatingHoursTypeEvent(
      operating_hours_type,
      String(form.values.address_label)
    ).sendEvent()
  }, [operating_hours_type])

  return (
    <Box {...props}>
      <Flex gap='sm' wrap='wrap'>
        <BusinessHourButton
          value='always_open'
          label={t('shared.modules.address.operating_hours_type_always_open')}
        />
        <BusinessHourButton
          value='everyday'
          label={t('shared.modules.address.operating_hours_type_everyday')}
        />
        <BusinessHourButton
          value='weekdays'
          label={t('shared.modules.address.operating_hours_type_weekday')}
        />
        <BusinessHourButton
          value='custom'
          label={t('shared.modules.address.operating_hours_type_custom')}
        />
      </Flex>
      <Box>{operating_hours_type === 'custom' && <BusinessSchedule />}</Box>
    </Box>
  )
}

function BusinessHourButton({
  value,
  label,
}: {
  value: CoreUserAddressOperatingHours
  label: string
}) {
  const form = useFormContext()
  const { value: currentValue } = form.getInputProps('operating_hours_type')

  return (
    <Button
      size='sm'
      radius='xs'
      variant={value === currentValue ? 'outline' : 'default'}
      leftSection={value === currentValue ? <Icon icon={faCheck} /> : undefined}
      onClick={() => form.setFieldValue('operating_hours_type', value)}
    >
      {label}
    </Button>
  )
}
